import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import {
  useMutation,
  useInfiniteQuery,
  InfiniteData,
  UseInfiniteQueryResult,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  CPTCodeSuggestionDTO,
  DeleteSectionTemplateRequestDTO,
  None,
  MipsResponseDTO,
  MasterTemplateDTO,
  VisitNoteDTO,
  VisitNoteSummaryDTO,
  VisitNoteBillingCodeDTO,
  SaveMasterTemplateRequestDTO,
  SectionTemplateDTO,
  SaveSectionTemplateRequestDTO,
  BillingCodeDTO,
  BillingCodeFavoriteRequestDTO,
  BillingCodeAliasRequestDTO,
  AppointmentInfoForVisitNoteDTO,
  TemplateListItemDTOPaginatedResponseDTO,
  TemplateListItemDTO,
  SaveVisitNoteSOAPRequestDTO,
  RetrieveSectionTemplateRequestDTO,
  SectionTemplateContentDTO,
} from 'dtos';
import {
  GetSectionTemplateContentsParams,
  VisitNote,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/types/visitNote';
import getMipsSuggestion from 'pages/Dashboard/pages/Encounters/pages/VisitNote/services/api';
import {
  formatPatient,
} from 'pages/Dashboard/pages/Charts/helper';
import isNil from 'lodash/isNil';
import {
  normalizeVisitNote,
  normalizePatientMedicalBackground,
  serializeVisitNote,
  visitNoteTextFields,
  normalizeServiceCodes,
  orderSectionTemplateContents,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  Patient,
  ReviewOfSystemElement,
} from 'pages/Dashboard/types/patient';
import {
  normalizeBillingCodes,
} from 'pages/Dashboard/pages/Billing/helpers';
import {
  isNilOrZero,
  notEmptyString,
} from '@populateui/utils';
import {
  deleteApiBillingCodeDeleteAlias,
  deleteApiTemplateDeleteMasterTemplate,
  deleteApiTemplateDeleteSectionTemplate,
  getApiPatientGetPatientMedicalBackgroundId,
  getApiTemplateRetrieveMasterTemplate,
  getApiTemplateRetrieveSectionTemplate,
  getApiVisitNoteGetAICodeSuggestionsEncounterIdCodeType,
  getApiVisitNoteGetAppointmentRelatedInfoByVisitNoteIdVisitNoteId,
  getApiVisitNoteGetHPIInParagraphForm,
  getApiVisitNoteGetSpecificVisitNoteForPatient,
  getApiVisitNoteGetVisitNoteByEncounterIdEncounterId,
  postApiBillingCodeCreateAlias,
  postApiTemplateSaveMasterTemplate,
  PostApiTemplateSaveMasterTemplateResult,
  postApiTemplateSaveSectionTemplate,
  postApiTemplateRetrieveSectionTemplates,
  postApiVisitNoteSaveVisitNote,
  putApiBillingCodeToggleFavorite,
  putApiVisitNoteSaveVisitNoteSOAP,
  getApiVisitNoteGetSpecificVisitNoteSectionForPatient,
  postApiVisitNoteUnFinalize,
  getApiVisitNoteGetPastVisitNotesForPatient,
} from 'endpoints';
import {
  GetQueryParams,
} from 'pages/Dashboard/types/common';
import {
  normalizeContent,
  serializeContent,
} from 'views/EMR/Settings/helper';
import {
  normalizeReviewOfSystem,
  normalizeSocialHistory,
  parseHeightInFeet,
} from 'pages/Dashboard/pages/Charts/pages/Details/utils/helper';
import {
  PatientMedicalBackgroundForm,
} from 'views/EMR/components/MedicalBackgroundDialog';
import {
  VisitNoteData,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/MainContent';
import {
  ServiceType,
} from 'views/EMR/components/ServiceLookup/type';
import {
  useCallback,
  RefObject,
  useEffect,
  useRef,
} from 'react';
import {
  useSnapActions,
  useSnapSoapNote,
} from 'views/EMR/stores/snap';
import {
  useParams,
} from 'react-router-dom';
import omit from 'lodash/omit';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useAppendToVisitNote,
} from 'pages/Dashboard/pages/Billing/hooks';
import {
  invalidateTemplate,
  templateKeys,
  templateListOptions,
  sectionTemplateOptions,
  TEMPLATE_GC_TIME,
  useTemplateQueryScope,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/templateQueries';

export function useSaveVisitNote():
  UseMutationResult<VisitNoteDTO, Error, VisitNoteData> {
  return useMutation<VisitNoteDTO, Error, VisitNoteData>({
    mutationFn: async (data: VisitNoteData) => {
      const response = await postApiVisitNoteSaveVisitNote(serializeVisitNote(data));
      return response;
    },
  });
}

export function useSaveVisitNoteSOAP():
  UseMutationResult<VisitNoteDTO, Error, SaveVisitNoteSOAPRequestDTO> {
  return useMutation<VisitNoteDTO, Error, SaveVisitNoteSOAPRequestDTO>({
    mutationFn: async (data) => {
      const serialized = {
        ...data,
        ...(mapValues(pick(data, visitNoteTextFields), serializeContent)),
      };
      return putApiVisitNoteSaveVisitNoteSOAP(serialized);
    },
  });
}

export function useUnfinalizeVisitNote(): UseMutationResult<VisitNoteDTO, Error, number> {
  return useMutation<VisitNoteDTO, Error, number>({
    mutationFn: (visitNoteId) => postApiVisitNoteUnFinalize({ visitNoteId }),
  });
}

export function useGetVisitNote(encounterId: number): UseQueryResult<VisitNote, Error> {
  return useQuery<VisitNote, Error>({
    queryKey: ['encounters', encounterId],
    queryFn: async () => {
      const data = await getApiVisitNoteGetVisitNoteByEncounterIdEncounterId(encounterId);
      const { bill, ...visitNote } = normalizeVisitNote(data);

      return {
        ...visitNote,
        patient: formatPatient(visitNote?.patient) as Patient,
        bill: {
          ...bill,
          serviceCodes: normalizeBillingCodes(
            (bill?.serviceCodes ?? []) as VisitNoteBillingCodeDTO[],
            {},
            bill?.assessmentCodes ?? [],
          ),
        },
      };
    },
    enabled: !isNilOrZero(encounterId),
  });
}

export function useGetVisitNoteRelatedInfo(
  visitNoteId: number = 0,
): UseQueryResult<AppointmentInfoForVisitNoteDTO, Error> {
  return useQuery<AppointmentInfoForVisitNoteDTO, Error>({
    queryKey: ['visit-note-related-info', visitNoteId],
    queryFn: () => getApiVisitNoteGetAppointmentRelatedInfoByVisitNoteIdVisitNoteId(visitNoteId),
    enabled: visitNoteId > 0,
  });
}

export type PatientMedicalBackground = {
  medications: string[];
  drugAllergies: string[];
  nonDrugAllergies: string[];
  medicalHistories: string[];
  surgicalHistories: string[];
  familyHistories: string[];
  socialHistory: string[];
  reviewOfSystem: ReviewOfSystemElement[];
  bmi: string[];
};

export function useGetPatientMedicalBackground(
  patientId: number,
): UseQueryResult<PatientMedicalBackground, Error> {
  return useQuery<PatientMedicalBackground, Error>({
    queryKey: ['patient-medical-background', patientId],
    queryFn: async () => {
      const data = await getApiPatientGetPatientMedicalBackgroundId(patientId);
      return normalizePatientMedicalBackground(data);
    },
    enabled: !isNilOrZero(patientId),
  });
}

export function useGetMedicalBackground(
  patientId: number,
  dateOfBirth?: string,
  enabled: boolean = true,
): UseQueryResult<PatientMedicalBackgroundForm, Error> {
  return useQuery<PatientMedicalBackgroundForm, Error>({
    queryKey: ['patient--medical-background', patientId, dateOfBirth],
    queryFn: async () => {
      const {
        socialHistory, reviewOfSystem, ...data
      } = await getApiPatientGetPatientMedicalBackgroundId(patientId);

      return {
        ...data,
        socialHistory: normalizeSocialHistory(socialHistory ?? {}, dateOfBirth ?? ''),
        groupedReviewOfSystemElements: normalizeReviewOfSystem(reviewOfSystem ?? {}),
        ...parseHeightInFeet(data?.height ?? 0),
      };
    },
    enabled: !isNilOrZero(patientId) && enabled,
  });
}

export function useGetPastVisitNotesForPatient(
  patientId: number,
  currentVisitNoteId: number,
): UseQueryResult<VisitNoteSummaryDTO[], Error> {
  return useQuery<VisitNoteSummaryDTO[], Error>({
    queryKey: ['pastVisitNotesForPatient', patientId, currentVisitNoteId],
    queryFn: () => getApiVisitNoteGetPastVisitNotesForPatient({
      patientId,
      currentVisitNoteId,
    }),
    enabled: ![currentVisitNoteId, patientId].some(isNilOrZero),
  });
}

export function useGetVisitNoteForPatient(
  visitNoteId: number | null,
  patientId: number | null,
): UseQueryResult<VisitNoteDTO, Error> {
  return useQuery<VisitNoteDTO, Error>({
    queryKey: ['priorVisitNoteForPatient', visitNoteId, patientId],
    queryFn: () => (getApiVisitNoteGetSpecificVisitNoteForPatient({
      visitNoteId: visitNoteId ?? 0,
      patientId: patientId ?? 0,
    })),
    enabled: ![visitNoteId, patientId].some(isNilOrZero),
  });
}

export function useGetSpecificVisitNoteSectionForPatient(
  visitNoteId: number,
  patientId: number,
  sectionName: string,
): UseQueryResult<string, Error> {
  return useQuery<string, Error>({
    queryKey: ['priorVisitNoteForPatient', visitNoteId, patientId, sectionName],
    queryFn: () => getApiVisitNoteGetSpecificVisitNoteSectionForPatient({
      visitNoteId,
      patientId,
      sectionName,
    }),
    enabled: false,
  });
}

export function useCreateAlias(): UseMutationResult<
  BillingCodeDTO | void, Error, BillingCodeAliasRequestDTO > {
  return useMutation< BillingCodeDTO | void, Error, BillingCodeAliasRequestDTO >({
    mutationFn: (data) => postApiBillingCodeCreateAlias(data),
  });
}

export function useDeleteAlias(): UseMutationResult<
  BillingCodeDTO | void, Error, BillingCodeAliasRequestDTO> {
  return useMutation<BillingCodeDTO | void, Error, BillingCodeAliasRequestDTO>({
    mutationFn: (data) => deleteApiBillingCodeDeleteAlias(data),
  });
}

export function useGetCodeSuggestions(
  encounterId: number,
  type: ServiceType,
): UseQueryResult<CPTCodeSuggestionDTO[], Error> {
  return useQuery<CPTCodeSuggestionDTO[], Error>({
    queryKey: ['CodeSuggestions', encounterId, type],
    queryFn: async () => {
      const response = await getApiVisitNoteGetAICodeSuggestionsEncounterIdCodeType(
        encounterId,
        type,
      );
      return normalizeServiceCodes(response?.codeSuggestions ?? [], type);
    },

    gcTime: Infinity,
    staleTime: Infinity,
    enabled: false,
  });
}

export function useGetHPIParagraph(encounterId: number): UseQueryResult<string, Error> {
  return useQuery<string, Error>({
    queryKey: ['hpiParagraph', encounterId],
    queryFn: () => getApiVisitNoteGetHPIInParagraphForm({ encounterId }),
    enabled: false,
  });
}

export function useGetMasterTemplates(
  params: Partial<GetQueryParams> & { sectionName?: string } = {},
): UseQueryResult<
  TemplateListItemDTOPaginatedResponseDTO,
  Error> & { fetchNextPage?: never } {
  const scope = useTemplateQueryScope();
  return useQuery(templateListOptions(scope, 'master', {
    searchText: params.searchQuery,
    ...params.pagination,
  }));
}

export function useGetMasterTemplateByName(templateName: string = ''): UseQueryResult<MasterTemplateDTO, Error> {
  const scope = useTemplateQueryScope();
  return useQuery<MasterTemplateDTO, Error>({
    queryKey: templateKeys.detail(scope, templateName),
    gcTime: TEMPLATE_GC_TIME,
    queryFn: async () => {
      const response = await getApiTemplateRetrieveMasterTemplate({ templateName });
      return {
        ...response,
        ...mapValues(pick(response, visitNoteTextFields), normalizeContent),
      };
    },
    enabled: notEmptyString(templateName),
  });
}

export function useGetSectionTemplateByName(
  templateName: string,
  sectionName: string,
): UseQueryResult<string, Error> {
  const scope = useTemplateQueryScope();
  return useQuery<string, Error>({
    queryKey: templateKeys.detail(scope, templateName, sectionName),
    gcTime: TEMPLATE_GC_TIME,
    queryFn: () => getApiTemplateRetrieveSectionTemplate({ templateName, sectionName }),
    enabled: notEmptyString(templateName) && notEmptyString(sectionName),
  });
}

export function useGetSectionTemplateContents(): UseMutationResult<
  string[], Error, GetSectionTemplateContentsParams
> {
  return useMutation<string[], Error, GetSectionTemplateContentsParams>({
    mutationFn: async ({ templateNames, sectionName }) => {
      const templates = templateNames.map((templateName) => ({
        templateName,
        sectionName,
      }));
      const contents = await postApiTemplateRetrieveSectionTemplates(templates);
      return orderSectionTemplateContents(templates, contents).map(({ content }) => content);
    },
  });
}

export function useGetBulkSectionTemplateContents(): UseMutationResult<
  SectionTemplateContentDTO[], Error, RetrieveSectionTemplateRequestDTO[]
> {
  return useMutation<
    SectionTemplateContentDTO[], Error, RetrieveSectionTemplateRequestDTO[]
  >({
    mutationFn: async (templates) => {
      const contents = await postApiTemplateRetrieveSectionTemplates(templates);
      return orderSectionTemplateContents(templates, contents);
    },
  });
}

export function useSaveMasterTemplate(): UseMutationResult<
  PostApiTemplateSaveMasterTemplateResult,
  Error,
  SaveMasterTemplateRequestDTO> {
  const queryClient = useQueryClient();
  const scope = useTemplateQueryScope();
  return useMutation<
    PostApiTemplateSaveMasterTemplateResult,
    Error,
    SaveMasterTemplateRequestDTO>({
      mutationFn: async (data) => {
        const response = await postApiTemplateSaveMasterTemplate({
          ...data,
          ...mapValues(pick(data, visitNoteTextFields), serializeContent),
        });
        return response;
      },
      onSuccess: (_, { templateName }) => invalidateTemplate(queryClient, scope, templateName ?? ''),
    });
}

export function useDeleteTemplate(): UseMutationResult<
  None, Error, Partial<DeleteSectionTemplateRequestDTO>> {
  const queryClient = useQueryClient();
  const scope = useTemplateQueryScope();
  return useMutation<None, Error, Partial<DeleteSectionTemplateRequestDTO>>({
    mutationFn: (data: Partial<DeleteSectionTemplateRequestDTO>) => (
      !isNil(data.sectionName)
        ? deleteApiTemplateDeleteSectionTemplate(data)
        : deleteApiTemplateDeleteMasterTemplate(data)
    ),
    onSuccess: (_, { templateName, sectionName }) => (
      invalidateTemplate(queryClient, scope, templateName ?? '', sectionName)
    ),
  });
}

export function useSaveSectionTemplate(): UseMutationResult<
  SectionTemplateDTO,
  Error,
  SaveSectionTemplateRequestDTO> {
  const queryClient = useQueryClient();
  const scope = useTemplateQueryScope();
  return useMutation<
    SectionTemplateDTO,
    Error,
    SaveSectionTemplateRequestDTO>({
      mutationFn: (data) => postApiTemplateSaveSectionTemplate(data),
      onSuccess: (_, { templateName, sectionName }) => (
        invalidateTemplate(queryClient, scope, templateName ?? '', sectionName)
      ),
    });
}

function selectSectionTemplatePages({ pages }: InfiniteData<TemplateListItemDTOPaginatedResponseDTO>) {
  return {
    data: pages.flatMap((page) => (
      (page.data ?? []).filter((template): template is TemplateListItemDTO & {
        sectionName: string; templateName: string;
      } => !!template.sectionName && !!template.templateName)
    )),
    pageProperties: pages.at(-1)?.pageProperties,
  };
}

export function useSectionTemplateOptions(
  sectionName?: string,
  searchText?: string,
): UseInfiniteQueryResult<ReturnType<typeof selectSectionTemplatePages>, Error> {
  const scope = useTemplateQueryScope();
  return useInfiniteQuery({
    ...sectionTemplateOptions(scope, sectionName, searchText),
    select: selectSectionTemplatePages,
  });
}

export function useGetSectionTemplates(
  params: { sectionName: string; searchQuery?: string },
) {
  return useSectionTemplateOptions(params.sectionName, params.searchQuery);
}

export function useTemplateHooks(section?: string) {
  return notEmptyString(section)
    ? {
      useGetList: useGetSectionTemplates,
      useGetByName: useGetSectionTemplateByName,
      useSave: useSaveSectionTemplate,
    }
    : {
      useGetList: useGetMasterTemplates,
      useGetByName: useGetMasterTemplateByName,
      useSave: useSaveMasterTemplate,
    };
}

export function useToggleFavorite(): UseMutationResult<
  BillingCodeDTO | void, Error, BillingCodeFavoriteRequestDTO> {
  return useMutation< BillingCodeDTO | void, Error, BillingCodeFavoriteRequestDTO >({
    mutationFn: (data) => putApiBillingCodeToggleFavorite(data),
  });
}

export function useGetMipsSuggestion(
  visitNoteId: number,
  enabled: boolean,
): UseQueryResult<MipsResponseDTO[], Error> {
  return useQuery<MipsResponseDTO[], Error>({
    queryKey: ['mips', visitNoteId],
    queryFn: () => getMipsSuggestion(visitNoteId),
    enabled,
  });
}

export function useSoapNote() {
  const soapNote = useSnapSoapNote();
  const soapNoteId = soapNote?.encounterId;

  const { setSoapNote } = useSnapActions();
  const { id = '' } = useParams<{ id: string }>();
  const encounterId = Number(id);

  const {
    appendTextFieldWithDoubleLine,
  } = useAppendToVisitNote();

  const handleSoapNote = useCallback(() => {
    if (!isNil(soapNote) && soapNoteId === Number(encounterId)) {
      const keys = Object.keys(omit(soapNote, 'encounterId')) as (keyof VisitNoteData)[];
      keys.forEach((key) => {
        appendTextFieldWithDoubleLine(key, soapNote[key] as string);
      });
      setSoapNote(null);
    }
  }, [soapNote, encounterId]);

  return { handleSoapNote };
}

type SectionField =
  | 'additionalHPI'
  | 'physicalExam'
  | 'radiology'
  | 'assessmentCodes'
  | 'additionalAssessment'
  | 'plan'
  | 'serviceCodes'
  | 'customServiceCodes';

type SectionRefs = Record<SectionField, RefObject<HTMLDivElement>>;

export function useSectionRefs(): SectionRefs {
  const refs = useRef<SectionRefs>({
    additionalHPI: { current: null! },
    physicalExam: { current: null! },
    radiology: { current: null! },
    assessmentCodes: { current: null! },
    additionalAssessment: { current: null! },
    plan: { current: null! },
    serviceCodes: { current: null! },
    customServiceCodes: { current: null! },
  });

  useEffect(() => {
    const unsubscribe = visitNoteEventChannel.on('onScrollToSection', (field) => {
      setTimeout(() => {
        refs.current[field as SectionField]?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 50);
    });
    return unsubscribe;
  }, []);

  return refs.current;
}
